import React from 'react';
import type { IFormElement } from '../types';
import styles from './fields.module.css';

export interface ExpressionFieldProps {
  element: IFormElement;
  formValues?: Record<string, any>;
  questionNumber?: number;
}

export const ExpressionField: React.FC<ExpressionFieldProps> = ({
  element,
  formValues = {},
  questionNumber,
}) => {
  const expression = element.expression || '';

  const evaluateExpression = (expr: string): string => {
    if (!expr) return '';

    let result = expr.replace(/\{([^}]+)\}/g, (_, fieldName) => {
      const value = formValues[fieldName];
      if (value === undefined || value === null) return '';
      return String(value);
    });

    try {
      if (/^[\d\s+\-*/().]+$/.test(result)) {
        // eslint-disable-next-line no-new-func
        const computed = new Function(`return ${result}`)();
        if (typeof computed === 'number' && !isNaN(computed)) {
          return String(computed);
        }
      }
    } catch {
      console.error(`Erro ao avaliar expressão: ${expr}`);
    }

    return result;
  };

  const displayValue = evaluateExpression(expression);
  const format = element.displayStyle || 'decimal';

  const formatValue = (value: string): string => {
    if (!value) return element.defaultValueExpression || '-';

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(numValue);
      case 'percent':
        return new Intl.NumberFormat('pt-BR', {
          style: 'percent',
          minimumFractionDigits: 2,
        }).format(numValue / 100);
      case 'decimal':
      default:
        return new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
    }
  };

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label className={styles.label}>
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <div
        style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--df-background-secondary, #f5f5f5)',
          borderRadius: '4px',
          fontWeight: 500,
          color: 'var(--df-text-primary, #1a1a1a)',
        }}
      >
        {formatValue(displayValue)}
      </div>
    </div>
  );
};

export default ExpressionField;
